import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Music, Eye, EyeOff, Users, Plus, ArrowLeft, Key, ArrowRight, Check } from "lucide-react";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // iter-134k: sys-admin impersonation. When present, we skip the
  // login form entirely — store the injected token as the auth
  // session, then bounce straight to the dashboard.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const impToken = params.get("impersonate_token");
    if (!impToken) return;
    let cancelled = false;
    (async () => {
      try {
        localStorage.setItem("token", impToken);
        // iter-135: decode the JWT to surface the impersonation banner
        // (target email + who initiated it) so the sys-admin can never
        // forget they're operating as another user.
        try {
          const payload = JSON.parse(
            atob(impToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
          );
          if (payload.impersonated_by) {
            localStorage.setItem(
              "impersonation",
              JSON.stringify({
                by: payload.impersonated_by,
                target_email: payload.impersonated_target_email || null,
                band_id: payload.impersonation_band_id || null,
                exp: payload.exp || null,
              })
            );
          }
        } catch (_) { /* malformed token — fall through */ }
        // iter-135 (post-tester fix): AuthProvider requires localStorage.user
        // to hydrate. Fetch /auth/me so the SPA mounts the authenticated
        // Layout (which owns the ImpersonationBanner) instead of the
        // marketing landing page.
        try {
          const me = await apiClient.get("/auth/me");
          if (!cancelled && me?.data) {
            localStorage.setItem("user", JSON.stringify(me.data));
          }
        } catch (_) { /* leave user unset — LoginGate will bounce */ }
      } finally {
        if (!cancelled) {
          // Full reload so AuthProvider re-reads localStorage and mounts
          // Layout + banner from scratch.
          window.location.replace("/");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);
  // Start in signup mode when ?signup=1 is present (landing page "Start free trial" CTAs).
  const initialMode = new URLSearchParams(location.search).get("signup") === "1" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    band_name: "",
    band_type: "british_brass",
    join_code: "",
    section_id: ""
  });
  // Band lookup state for join-band flow
  const [bandLookup, setBandLookup] = useState({ status: "idle", band_name: "", sections: [], band_id: "", logo_url: null });
  // GDPR consents (for signup flows only)
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
    notifications: false,
    data_sharing: false,
  });
  
  const { login, register } = useAuth();

  // Look up the band (name + sections + logo) when the user types a full 6-char join code
  useEffect(() => {
    if (mode !== "join-band") {
      setBandLookup({ status: "idle", band_name: "", sections: [], band_id: "", logo_url: null });
      return;
    }
    const code = (formData.join_code || "").trim().toUpperCase();
    if (code.length !== 6) {
      setBandLookup({ status: "idle", band_name: "", sections: [], band_id: "", logo_url: null });
      return;
    }
    let cancelled = false;
    setBandLookup((prev) => ({ ...prev, status: "loading" }));
    apiClient
      .get(`/auth/band-info?code=${encodeURIComponent(code)}`)
      .then((res) => {
        if (cancelled) return;
        setBandLookup({
          status: "ready",
          band_name: res.data.band_name,
          band_id: res.data.band_id,
          logo_url: res.data.logo_url,
          sections: res.data.sections || []
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBandLookup({ status: "invalid", band_name: "", sections: [], band_id: "", logo_url: null });
        setFormData((prev) => ({ ...prev, section_id: "" }));
      });
    return () => {
      cancelled = true;
    };
  }, [mode, formData.join_code]);

  const bandTypes = [
    { value: "british_brass", label: "British Brass Band" },
    { value: "concert_band", label: "Concert Band / Wind Band" },
    { value: "orchestra", label: "Orchestra" },
    { value: "big_band", label: "Big Band / Jazz Band" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
        navigate("/");
      } else if (mode === "create-band") {
        if (!formData.band_name.trim()) {
          toast.error("Please enter a band name");
          setLoading(false);
          return;
        }
        if (!consents.terms || !consents.privacy) {
          toast.error("Please accept the Terms of Service and Privacy Policy to continue");
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password, formData.band_name, null, formData.band_type, null, consents);
        toast.success(`Band "${formData.band_name}" created! You are now the admin.`);
        navigate("/");
      } else if (mode === "join-band") {
        if (!formData.join_code.trim()) {
          toast.error("Please enter a band code");
          setLoading(false);
          return;
        }
        if (bandLookup.status !== "ready") {
          toast.error("Please enter a valid band code");
          setLoading(false);
          return;
        }
        if (!formData.section_id) {
          toast.error("Please select your section");
          setLoading(false);
          return;
        }
        if (!consents.terms || !consents.privacy) {
          toast.error("Please accept the Terms of Service and Privacy Policy to continue");
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password, null, formData.join_code, null, formData.section_id, consents);
        toast.success("Account created! You've joined the band.");
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const renderRegisterOptions = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground text-center mb-8">
        Choose how you'd like to get started
      </p>
      
      <button
        type="button"
        className="w-full p-5 rounded-xl bg-card/50 border border-white/[0.06] hover:border-primary/30 hover:bg-primary/5 transition-all duration-300 flex items-start gap-4 text-left group"
        onClick={() => setMode("create-band")}
        data-testid="create-band-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
          <Plus className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground mb-1">Create a New Band</p>
          <p className="text-xs text-muted-foreground leading-relaxed">Start fresh with your own band. You'll be the admin with full control.</p>
        </div>
        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors mt-1" />
      </button>
      
      <button
        type="button"
        className="w-full p-5 rounded-xl bg-card/50 border border-white/[0.06] hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300 flex items-start gap-4 text-left group"
        onClick={() => setMode("join-band")}
        data-testid="join-band-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
          <Key className="w-6 h-6 text-blue-400" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground mb-1">Join with Band Code</p>
          <p className="text-xs text-muted-foreground leading-relaxed">Enter the 6-character code from your band admin to join.</p>
        </div>
        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-400 transition-colors mt-1" />
      </button>
      
      <div className="text-center pt-6">
        <button
          type="button"
          onClick={() => setMode("login")}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Already have an account? <span className="text-primary font-semibold">Sign in</span>
        </button>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-4">
        {mode !== "login" && (
          <>
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Full Name
              </Label>
              <Input
                id="name"
                data-testid="register-name-input"
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-11 bg-secondary/50 border-white/[0.06] focus:border-primary/50 focus:bg-secondary/80 transition-all"
                required
              />
            </div>
            
            {mode === "create-band" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="band_name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Band Name
                  </Label>
                  <Input
                    id="band_name"
                    data-testid="band-name-input"
                    type="text"
                    placeholder="e.g., City Brass Band"
                    value={formData.band_name}
                    onChange={(e) => setFormData({ ...formData, band_name: e.target.value })}
                    className="h-11 bg-secondary/50 border-white/[0.06] focus:border-primary/50 focus:bg-secondary/80 transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="band_type" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Ensemble Type
                  </Label>
                  <Select 
                    value={formData.band_type} 
                    onValueChange={(value) => setFormData({ ...formData, band_type: value })}
                  >
                    <SelectTrigger className="h-11 bg-secondary/50 border-white/[0.06] focus:border-primary/50" data-testid="band-type-select">
                      <SelectValue placeholder="Select band type" />
                    </SelectTrigger>
                    <SelectContent>
                      {bandTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    This will set up the standard sections for your ensemble
                  </p>
                </div>
              </>
            )}
            
            {mode === "join-band" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="join_code" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Band Code
                  </Label>
                  <Input
                    id="join_code"
                    data-testid="join-code-input"
                    type="text"
                    placeholder="ABC123"
                    value={formData.join_code}
                    onChange={(e) => setFormData({ ...formData, join_code: e.target.value.toUpperCase() })}
                    className="h-12 bg-secondary/50 border-white/[0.06] focus:border-primary/50 font-mono text-xl tracking-[0.3em] text-center uppercase"
                    maxLength={6}
                    required
                  />
                  <p className="text-[11px] text-muted-foreground text-center">
                    Ask your band administrator for the 6-character code
                  </p>
                  {bandLookup.status === "loading" && (
                    <p className="text-[11px] text-center text-muted-foreground" data-testid="band-lookup-loading">Looking up band…</p>
                  )}
                  {bandLookup.status === "ready" && (
                    <div className="flex flex-col items-center gap-2" data-testid="band-lookup-ready">
                      {bandLookup.logo_url && (
                        <img
                          src={`${BACKEND_URL}${bandLookup.logo_url}`}
                          alt={`${bandLookup.band_name} logo`}
                          className="h-16 w-auto max-w-[160px] rounded-lg object-contain"
                          data-testid="band-lookup-logo"
                        />
                      )}
                      <p className="text-[11px] text-center text-green-400 flex items-center justify-center gap-1">
                        <Check className="w-3 h-3" /> Found: <strong>{bandLookup.band_name}</strong>
                      </p>
                    </div>
                  )}
                  {bandLookup.status === "invalid" && (
                    <p className="text-[11px] text-center text-red-400" data-testid="band-lookup-invalid">
                      Invalid band code. Please check and try again.
                    </p>
                  )}
                </div>
                
                {bandLookup.status === "ready" && (
                  <div className="space-y-2" data-testid="join-section-wrapper">
                    <Label htmlFor="section_id" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Your Section <span className="text-red-400">*</span>
                    </Label>
                    <Select 
                      value={formData.section_id} 
                      onValueChange={(value) => setFormData({ ...formData, section_id: value })}
                    >
                      <SelectTrigger className="h-11 bg-secondary/50 border-white/[0.06] focus:border-primary/50" data-testid="join-section-select">
                        <SelectValue placeholder="Select the section you play in" />
                      </SelectTrigger>
                      <SelectContent>
                        {bandLookup.sections.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`join-section-option-${s.id}`}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      Admins can change your section later.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Email Address
          </Label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="h-11 bg-secondary/50 border-white/[0.06] focus:border-primary/50 focus:bg-secondary/80 transition-all"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              data-testid="login-password-input"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="h-11 bg-secondary/50 border-white/[0.06] focus:border-primary/50 focus:bg-secondary/80 transition-all pr-11"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {mode === "login" && (
            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* GDPR consents — shown on signup flows only */}
      {mode !== "login" && (
        <div className="space-y-3 border-t border-white/5 pt-4" data-testid="consent-block">
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={consents.terms}
              onChange={(e) => setConsents({ ...consents, terms: e.target.checked })}
              className="w-4 h-4 mt-0.5 accent-primary flex-shrink-0"
              data-testid="consent-terms"
            />
            <span>
              I have read and agree to the <Link to="/terms" target="_blank" className="text-primary underline underline-offset-2">Terms of Service</Link>. <span className="text-rose-400">*</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={consents.privacy}
              onChange={(e) => setConsents({ ...consents, privacy: e.target.checked })}
              className="w-4 h-4 mt-0.5 accent-primary flex-shrink-0"
              data-testid="consent-privacy"
            />
            <span>
              I have read and agree to the <Link to="/privacy" target="_blank" className="text-primary underline underline-offset-2">Privacy Policy</Link>. <span className="text-rose-400">*</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={consents.notifications}
              onChange={(e) => setConsents({ ...consents, notifications: e.target.checked })}
              className="w-4 h-4 mt-0.5 accent-primary flex-shrink-0"
              data-testid="consent-notifications"
            />
            <span className="text-muted-foreground">
              (Optional) I consent to receiving event reminder emails and push notifications.
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={consents.data_sharing}
              onChange={(e) => setConsents({ ...consents, data_sharing: e.target.checked })}
              className="w-4 h-4 mt-0.5 accent-primary flex-shrink-0"
              data-testid="consent-data-sharing"
            />
            <span className="text-muted-foreground">
              (Optional) I consent to my name, email and contact info being shared with my band admins so they can administer my membership.
            </span>
          </label>
        </div>
      )}

      <Button
        type="submit"
        data-testid="login-submit-btn"
        className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold btn-glow transition-all duration-300"
        disabled={loading || (mode !== "login" && (!consents.terms || !consents.privacy))}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            Please wait...
          </span>
        ) : (
          mode === "login" ? "Sign In" : 
          mode === "create-band" ? "Create Band & Account" : 
          "Join Band & Create Account"
        )}
      </Button>
      
      {mode !== "login" && (
        <Button
          type="button"
          variant="ghost"
          className="w-full h-10 text-muted-foreground hover:text-foreground"
          onClick={() => setMode("register")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to options
        </Button>
      )}
    </form>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-[480px] xl:w-[520px] flex flex-col justify-center p-8 lg:p-12 relative">
        <div className="spotlight absolute inset-0 pointer-events-none opacity-60" />
        
        <div className="w-full max-w-[380px] mx-auto space-y-8 relative z-10 animate-fade-in">
          {/* Logo */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 mb-6">
              <Music className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">
              Vivo
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Musician Management</p>
            <p className="mt-4 text-sm text-foreground">
              {mode === "login" && "Sign in to your account"}
              {mode === "register" && "Get started with Vivo"}
              {mode === "create-band" && "Create your band"}
              {mode === "join-band" && "Join with band code"}
            </p>
          </div>

          {/* Form Content */}
          {mode === "register" ? renderRegisterOptions() : renderForm()}

          {/* Toggle Login/Register */}
          {mode === "login" && (
            <div className="text-center lg:text-left">
              <button
                type="button"
                data-testid="toggle-auth-mode-btn"
                onClick={() => setMode("register")}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Don't have an account? <span className="text-primary font-semibold">Sign up</span>
              </button>
            </div>
          )}

          {/* Legal footer — required for GDPR transparency */}
          <div className="border-t border-white/5 pt-5 text-center lg:text-left" data-testid="login-legal-footer">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground justify-center lg:justify-start">
              <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy</Link>
              <Link to="/cookies" className="hover:text-foreground transition-colors" data-testid="footer-cookies-link">Cookies</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms</Link>
              <Link to="/support" className="hover:text-foreground transition-colors" data-testid="footer-support-link">Support</Link>
              <a href="mailto:james@support.vivomusicianmanagement.co.uk" className="hover:text-foreground transition-colors" data-testid="footer-contact-link">Contact</a>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              © {new Date().getFullYear()} Vivo Musician Management · James Fiddeman
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden items-center justify-center">
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50 z-10" />
        
        {/* Background image */}
        <img
          src="https://images.unsplash.com/photo-1762943627642-0f191e765ca9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxicmFzcyUyMGJhbmQlMjBwZXJmb3JtYW5jZXxlbnwwfHx8fDE3Njg5MjUzNTB8MA&ixlib=rb-4.1.0&q=85"
          alt="Brass instruments"
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Quote card */}
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <div className="glass rounded-2xl p-6 max-w-md">
            <p className="text-lg font-heading text-foreground leading-relaxed">
              "Music is the universal language of mankind."
            </p>
            <p className="text-sm text-muted-foreground mt-3 font-medium">
              — Henry Wadsworth Longfellow
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-12 right-12 z-20">
          <div className="flex items-center gap-2 glass rounded-full px-4 py-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-medium text-muted-foreground">Trusted by 500+ bands</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
